import { useState } from 'react'
import Alert from 'react-bootstrap/Alert'
import Badge from 'react-bootstrap/Badge'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import ListGroup from 'react-bootstrap/ListGroup'
import Modal from 'react-bootstrap/Modal'
import Spinner from 'react-bootstrap/Spinner'
import { deleteReceipt, type GroupDetail, type Receipt } from '../../../../types'
import { actionApiCall } from '../../api/actionApiCall'
import { useCurrencies } from '../../hooks/useCurrencies'
import { lzePorovnat, seRozpoznava, useInvalidateReceiptQueue, useReceiptQueue } from '../../hooks/useReceipts'
import { ReceiptAnalysesModal } from '../receipts/ReceiptAnalysesModal'
import { ReceiptRecheckMenu } from '../receipts/ReceiptRecheckMenu'
import { formatDateTime } from '../../utils/format'
import { popisFotek } from './ReceiptUploadGrouping'
import { exponentOf, formatMoney } from '../../utils/money'

export type ReceiptQueueModalProps = {
    show: boolean
    group: GroupDetail
    /** Účtenky, které si už převzal otevřený dialog pohybu - ve frontě se nenabízejí. */
    skrytaIds?: string[]
    /** Jak se pojmenuje potvrzovací tlačítko - podle toho, co s výběrem volající udělá. */
    potvrzeni: string
    onConfirm: (receipts: Receipt[]) => void
    onClose: () => void
}

/**
 * Výběr z fronty nahraných účtenek, které zatím nepatří k žádnému pohybu -
 * při úpravě pohybu k němu jde účtenku připojit místo jejího potvrzení.
 */
export function ReceiptQueueModal({ show, group, skrytaIds = [], potvrzeni, onConfirm, onClose }: ReceiptQueueModalProps) {
    const { data: currencies } = useCurrencies()
    const { data: fronta, isLoading } = useReceiptQueue(group.id, show)
    const invalidateFrontu = useInvalidateReceiptQueue()

    const [vybrane, setVybrane] = useState<string[]>([])

    const uctenky = (fronta ?? []).filter((receipt) => !skrytaIds.includes(receipt.id))

    const prepni = (receiptId: string, checked: boolean) => {
        setVybrane((predchozi) => (checked ? [...predchozi, receiptId] : predchozi.filter((id) => id !== receiptId)))
    }

    const smaz = async (receipt: Receipt) => {
        if (!window.confirm('Opravdu účtenku smazat i s fotkou? Tuto akci nelze vrátit.')) {
            return
        }
        const vysledek = await actionApiCall(() => deleteReceipt({ path: { receiptId: receipt.id } }), {
            successMessage: 'Účtenka byla smazána.',
        })
        if (vysledek !== undefined) {
            setVybrane((predchozi) => predchozi.filter((id) => id !== receipt.id))
            await invalidateFrontu(group.id)
        }
    }

    // Účtenka, jejíž výsledky rozpoznání se porovnávají.
    const [porovnavana, setPorovnavana] = useState<Receipt>()

    const potvrd = () => {
        onConfirm(uctenky.filter((receipt) => vybrane.includes(receipt.id)))
        setVybrane([])
    }

    return (
        <Modal show={show} onHide={onClose} centered scrollable size="lg">
            <Modal.Header closeButton>
                <Modal.Title as="h2" className="h5">Fronta účtenek</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <p className="text-secondary small">
                    Účtenky nahrané do skupiny, ze kterých zatím nevznikla útrata. Místo potvrzení v sekci Ke kontrole
                    je můžete připojit k tomuto pohybu. Nepotvrzené účtenky se po 14 dnech smažou.
                </p>

                {isLoading && (
                    <div className="text-secondary small">
                        <Spinner animation="border" size="sm" className="me-2" />
                        Načítám frontu…
                    </div>
                )}

                {!isLoading && uctenky.length === 0 && (
                    <Alert variant="light" className="border mb-0">
                        Ve frontě nečeká žádná účtenka.
                    </Alert>
                )}

                {uctenky.length > 0 && (
                    <ListGroup data-testid="fronta-uctenek">
                        {uctenky.map((receipt) => {
                            const mena = receipt.detectedCurrencyCode ?? group.baseCurrencyCode
                            const bezi = seRozpoznava(receipt)

                            return (
                                <ListGroup.Item key={receipt.id} className="d-flex align-items-start gap-2">
                                    <Form.Check
                                        type="checkbox"
                                        className="mt-1"
                                        id={`fronta-${receipt.id}`}
                                        checked={vybrane.includes(receipt.id)}
                                        aria-label={`Vybrat účtenku nahranou ${formatDateTime(receipt.createdAt)}`}
                                        onChange={(event) => prepni(receipt.id, event.target.checked)}
                                    />
                                    {/* Fotka se odkazuje přímo URL - přes fetch by se musela převádět na blob. */}
                                    <img
                                        src={`/api/receipts/${receipt.id}/file?variant=thumb`}
                                        alt=""
                                        className="rounded border flex-shrink-0"
                                        style={{ width: '3.5rem', height: '3.5rem', objectFit: 'cover' }}
                                    />

                                    <div className="flex-grow-1 min-w-0">
                                        <div className="text-truncate fw-semibold">
                                            {receipt.detectedTitle ?? 'Nerozpoznaný obchod'}
                                        </div>
                                        <div className="small text-secondary">
                                            {receipt.detectedTotal !== null && receipt.detectedTotal !== undefined
                                                ? formatMoney(receipt.detectedTotal, mena, exponentOf(currencies, mena))
                                                : 'Částka nerozpoznána'}
                                            {receipt.items.length > 0 && ` · ${receipt.items.length} ${pocetPolozek(receipt.items.length)}`}
                                            {receipt.pageCount > 1 && ` · ${popisFotek(receipt.pageCount)}`}
                                        </div>
                                        <div className="small text-secondary">Nahráno {formatDateTime(receipt.createdAt)}</div>
                                        <div className="mt-1 d-flex align-items-center gap-2 flex-wrap">
                                            <StavFronty receipt={receipt} />
                                        </div>
                                        {receipt.analysisStatus !== 'RUNNING' && (
                                            <div className="mt-2 d-flex align-items-center gap-2 flex-wrap">
                                                <ReceiptRecheckMenu
                                                    receipt={receipt}
                                                    size="sm"
                                                    onRequested={() => void invalidateFrontu(group.id)}
                                                />
                                                {lzePorovnat(receipt) && (
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        className="su-tappable"
                                                        onClick={() => setPorovnavana(receipt)}
                                                    >
                                                        <i className="bi bi-layout-split me-1" aria-hidden="true" />
                                                        Porovnat ({receipt.analysisCount})
                                                    </Button>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    <div className="d-flex align-items-center gap-2 flex-shrink-0">
                                        {bezi && <Spinner animation="border" size="sm" className="text-secondary" />}
                                        <Button
                                            variant="link"
                                            size="sm"
                                            className="text-danger p-0"
                                            aria-label="Smazat účtenku z fronty"
                                            onClick={() => void smaz(receipt)}
                                        >
                                            <i className="bi bi-trash" aria-hidden="true" />
                                        </Button>
                                    </div>
                                </ListGroup.Item>
                            )
                        })}
                    </ListGroup>
                )}
            </Modal.Body>

            <Modal.Footer className="justify-content-between">
                <Button variant="outline-secondary" onClick={onClose} className="su-tappable">
                    Zavřít
                </Button>
                <Button className="su-tappable" disabled={vybrane.length === 0} onClick={potvrd}>
                    {potvrzeni}
                    {vybrane.length > 0 && ` (${vybrane.length})`}
                </Button>
            </Modal.Footer>

            {porovnavana && (
                <ReceiptAnalysesModal
                    show={!!porovnavana}
                    receipt={porovnavana}
                    onClose={() => setPorovnavana(undefined)}
                    onApplied={() => void invalidateFrontu(group.id)}
                />
            )}
        </Modal>
    )
}

/** Stav rozpoznávání účtenky ve frontě. */
function StavFronty({ receipt }: { receipt: Receipt }) {
    if (receipt.analysisStatus === 'PENDING') {
        return <Badge bg="secondary">Čeká ve frontě</Badge>
    }
    if (receipt.analysisStatus === 'RUNNING') {
        return <Badge bg="secondary">Rozpoznávám</Badge>
    }
    if (receipt.analysisStatus === 'AWAITING_MCP') {
        return <Badge bg="info" text="dark">Čeká na AI asistenta (MCP)</Badge>
    }
    if (receipt.awaitingMcp && receipt.analysisStatus === 'DONE') {
        return <Badge bg="info" text="dark">Rozpoznáno · čeká i na AI asistenta</Badge>
    }
    if (receipt.analysisStatus === 'DONE') {
        return <Badge bg="success">Rozpoznáno</Badge>
    }
    if (receipt.analysisStatus === 'FAILED') {
        return (
            <Badge bg="warning" text="dark" title={receipt.analysisError ?? undefined}>
                Nerozpoznáno — doplňte ručně
            </Badge>
        )
    }
    return <Badge bg="secondary">Údaje doplňte ručně</Badge>
}

function pocetPolozek(count: number): string {
    if (count === 1) {
        return 'položka'
    }
    if (count >= 2 && count <= 4) {
        return 'položky'
    }
    return 'položek'
}
